import type { IVector3 } from '@seddi/math-v2';
import { SurfaceMaterialModel } from '@seddi/surface-material';
import {
  type LoadingManager,
  type Texture,
  type WebGLRenderer,
  CustomBlending,
  MeshPhysicalMaterial,
  OneFactor,
  RepeatWrapping,
  RGBAFormat,
} from '@seddi/three';
import type { RecolorContext } from '@seddi/ui-recolor';
import { produce } from 'immer';
import log from 'loglevel';
import type { SurfaceMaterialDesc, TextureDesc } from '../domain/materialTypes';
import * as FlagsModule from '../flags/flagsModule';
import { FeatureFlag } from '../flags/flagsTypes';
import { configure as configureFabric } from '../materials/fabricMaterial';
import { configure as configureLogo } from '../materials/logoMaterial';
import { materialMapsKeys } from '../three/materials/materialConstants';
import { load as loadTexture } from './textureLoader';
import { generateTextureMips } from './textureMipmaps';

type LoadOptions = { mipmapsHighQuality?: boolean };

export const ParamsMapDisney = {
  alphaMap: 'alphaMap',
  baseColor: 'color',
  baseColorMap: 'map',
  displacementMap: 'heightMap',
  metallic: 'metalness',
  metallicMap: 'metalnessMap',
  normalMap: 'normalMap',
  roughness: 'roughness',
  roughnessMap: 'roughnessMap',
  specular: 'specularIntensity',
  specularMap: 'specularIntensityMap',
};

const ParamsMap = ParamsMapDisney;

/**
 * Load material.
 * @param desc - Material.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 * @param options - Load configuration.
 */
export const load = async (
  desc: SurfaceMaterialDesc,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  options?: LoadOptions,
): Promise<MeshPhysicalMaterial | undefined> => {
  const { material, textures } = load1st(desc);

  // load textures
  const textureTags = desc.tags;

  const promises = textures.map(async ({ key, value: textureDesc }) => {
    let texture;
    switch (key) {
      case 'baseColorMap':
      case 'normalMap':
      case 'roughnessMap':
      case 'specularMap': {
        texture = await loadTextureEx(textureDesc, manager, renderer, textureTags, options);
        break;
      }
      default: {
        texture = await loadTexture(textureDesc, manager, renderer);
        break;
      }
    }
    material[ParamsMap[key]] = texture;
  });
  await Promise.all(promises);

  load2nd(desc, material);

  return material;
};

/**
 * Load material with albedo recolor.
 * @param desc - Material.
 * @param color - Target color.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 * @param recolor - Recolor context.
 * @param options - Load configuration.
 */
export const loadRecolor = async (
  desc: SurfaceMaterialDesc,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  color: IVector3,
  recolor: RecolorContext,
  options?: LoadOptions,
): Promise<MeshPhysicalMaterial | undefined> => {
  const { material, textures } = load1st(desc);

  // load textures
  const textureTags = desc.tags;

  const promises = textures.map(async ({ key, value: textureDesc }) => {
    let texture;
    switch (key) {
      case 'baseColorMap': {
        texture = await loadTextureWithRecolor(
          textureDesc,
          manager,
          renderer,
          color,
          recolor,
          textureTags,
          options,
        );
        break;
      }
      case 'normalMap':
      case 'roughnessMap':
      case 'specularMap': {
        texture = await loadTextureEx(textureDesc, manager, renderer, textureTags, options);
        break;
      }
      default: {
        texture = await loadTexture(textureDesc, manager, renderer);
        break;
      }
    }
    // Compute custom mipmap levels for the albedo and normal maps.
    material[ParamsMap[key]] = texture;
  });
  await Promise.all(promises);

  load2nd(desc, material);

  return material;
};

const load1st = (desc: SurfaceMaterialDesc) => {
  // only support Disney 2012
  if (desc.model !== SurfaceMaterialModel.DISNEY) {
    log.warn(`Unsuported shading model ${desc.id}`);
  }

  const material = new MeshPhysicalMaterial({});
  const textures: { key: string; value: TextureDesc }[] = [];

  Object.keys(desc.params).forEach((key) => {
    switch (key) {
      case 'baseColor': {
        const value = desc.params[key];
        material[ParamsMap[key]].setRGB(value.x, value.y, value.z);
        break;
      }
      case 'metallic':
      case 'roughness':
      case 'specular': {
        const value = desc.params[key];
        material[ParamsMap[key]] = value;
        break;
      }
      case 'alphaMap':
      case 'baseColorMap':
      case 'metallicMap':
      case 'normalMap':
      case 'roughnessMap':
      case 'specularMap': {
        const value = desc.params[key] as TextureDesc;
        textures.push({ key, value });
        break;
      }
      case 'displacementMap': {
        if (FlagsModule.isEnabled(FeatureFlag.PARALLAX)) {
          const value = desc.params[key] as TextureDesc;
          textures.push({ key, value });
        }
        break;
      }
      default:
        break;
    }
  });

  // TODO: Alpha included in baseColorMap
  if (desc.params.alphaMap) {
    material.transparent = true;
  }

  if (desc.tags?.includes('FABRIC')) {
    configureFabric(material);
  }

  if (desc.tags?.includes('LOGO')) {
    configureLogo(material);
  }

  return { material, textures };
};

const load2nd = (desc: SurfaceMaterialDesc, material: MeshPhysicalMaterial) => {
  // alpha map inside base color
  if (!desc.tags?.includes('FABRIC') && material.map?.format === RGBAFormat) {
    material.transparent = true;
  }

  // repeat
  const { repeat } = desc;
  if (repeat) {
    material.alphaMap?.repeat.copy(repeat);
    material.map?.repeat.copy(repeat);
    material.metalnessMap?.repeat.copy(repeat);
    material.normalMap?.repeat.copy(repeat);
    material.roughnessMap?.repeat.copy(repeat);
    material.specularIntensityMap?.repeat.copy(repeat);
  }

  if (desc.tags?.includes('FABRIC')) {
    // enable repeat wrapping on textures
    for (const key of materialMapsKeys) {
      const texture = material[key] as Texture | undefined;
      if (!texture) continue;

      texture.wrapS = RepeatWrapping;
      texture.wrapT = RepeatWrapping;
    }
  }
};

const loadTextureEx = async (
  desc: TextureDesc,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  tags?: string[],
  options?: LoadOptions,
) => {
  // Enable premultiplied alpha texture load.
  const usePremultipliedAlpha =
    FlagsModule.isEnabled(FeatureFlag.PREMULTIPLIED_ALPHA_LOGOS) && tags?.includes('LOGO');

  // DEBUG: Do not transform already premultiplied images.
  // const usePremultipliedAlpha =
  //   FlagsModule.isEnabled(FeatureFlag.PREMULTIPLIED_ALPHA_LOGOS) &&
  //   tags?.includes('LOGO') &&
  //   !desc.href.includes('pma.png');

  const texture = await loadTexture(desc, manager, renderer, {
    premultiplyAlpha: usePremultipliedAlpha ? 'premultiply' : 'none',
  });

  // Runtime generation of high-quality mipmaps.
  {
    const useMipmapsHighQuality = options?.mipmapsHighQuality ?? false;
    if (useMipmapsHighQuality) {
      await generateTextureMips(texture, tags);
    }
  }

  return texture;
};

const loadTextureWithRecolor = async (
  desc: TextureDesc,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  color: IVector3,
  recolorContext: RecolorContext,
  tags?: string[],
  options?: LoadOptions,
) => {
  // Runtime generation of a recolored image.
  const id = desc.id;
  await recolorContext.loadEx(id, desc.href, color);
  const recolor = recolorContext.getEx(id, color)!;

  // Load texture with a recolored image and mipmap.
  const recolorDesc = produce(desc, (desc) => {
    desc.href = recolor.blobUrl;
  });
  return loadTextureEx(recolorDesc, manager, renderer, tags, options);
};
